import  { useState, forwardRef } from 'react';
import classnames from 'classnames';
import { ByzzerButton,WithValue } from '@byzzer/ui-components';
import { useUser } from '@/contexts/UserContext';
import MarkdownEditor from '@uiw/react-markdown-editor';
import './SupportContent.scss';
// Images can be moved to cdn bucket
import handshake from '@images/icons/userSupportPreference/NIQ_Symbols_handshake.png';
import compass from '@images/icons/userSupportPreference/NIQ_compass.png';
import thumbsup from '@images/icons/userSupportPreference/NIQ_Symbols_Thumbs_Up.png';

const baseClassName = 'byz-support-content';

const UserSupportOptions = [
    {
        code: 'Schedule_meet',
        iconUrl: handshake,
        title: 'Help me get started',
        description: 'Schedule a meeting for a walk through of Byzzer.',
    },
    {
        code: 'take_tour',
        iconUrl: compass,
        title: 'Take a tour',
        description: 'Take a quick product tour to get started! Use the chat for live support as needed.',
    },
    {
        code: 'help_not_needed',
        iconUrl: thumbsup,
        title: `I've got this`,
        description: `Don't need help? That's great! If you change your mind, reach out to us in the Chat.`,
    },
];

type SupportDetails = {
    code: string;
    iconUrl: string;
    title: string;
    description: string;
};

type SupportCardProps = {
    supportOption: SupportDetails;
    selected?: boolean;
    onClick?: (code: string) => void;
};

function SupportCard({ supportOption, selected, onClick }: SupportCardProps) {
    const { title, description, iconUrl, code } = supportOption;
    function handleClick() {
        onClick?.(code);
    }
    return (
        <div
            className={classnames(`${baseClassName}__support-card`, {
                [`${baseClassName}__support-card--selected`]: selected,
            })}
            onClick={handleClick}
        >
            <img className={classnames(`${baseClassName}__support-card__thumbnail`)} src={iconUrl!} />{' '}
            {/* switch to pure css */}
            <div className={classnames(`${baseClassName}__support-card__title`)}>
                <MarkdownEditor.Markdown source={title} />
            </div>
            <div className={classnames(`${baseClassName}__support-card__description`)}>
                <MarkdownEditor.Markdown source={description} />
            </div>
        </div>
    );
}

function SupportCards({ selectedOption, onClick }: { selectedOption: string; onClick: (supportCode: string) => void }) {
    const { company } = useUser();
    function handleClick(supportCode: string) {
        onClick(supportCode);
    }
    return (
        <>
            {UserSupportOptions.map((option) => (
                <SupportCard
                    key={option.code}
                    supportOption={option}
                    onClick={handleClick}
                    selected={selectedOption?.includes(option.code)}
                />
            ))}
        </>
    );
}

export type SupportContentProps = {
    className?: string;
    onSubmit?: (selection: string) => Promise<void>;
    onSkip?: () => Promise<void>;
    includeSkip?: boolean;
    onChange?: (event: ByzzerChangeEvent<string>) => void;
    value?: string[];
    code?: string;
};

export const SupportContent = forwardRef<WithValue<string>, SupportContentProps>(
    ({ className, onSubmit, onChange, onSkip, includeSkip, value, code, ...props }, ref) => {
        const [internalValue, setInternalValue] = useState('Schedule_meet');
        const [busy, setBusy] = useState(false);

        const showSkipButton = includeSkip;

        function handleOptionCardClick(clickedOptionCode: string) {
            let newOption;
            newOption = clickedOptionCode;
            if (onChange) {
                onChange(newOption);
            } else {
                setInternalValue(newOption);
            }
        }

        async function handleSubmitClick() {
            setBusy(true);
            try {
                await onSubmit?.(internalValue);
            } catch (error) {
                throw error;
            } finally {
                setBusy(false);
            }
        }

        async function handleSkipClick() {
            try {
                await onSkip?.();
            } catch (error) {
                console.error(`SupportContent - handleSkipClick fired - error ===>>`, error);
                throw error;
            }
        }

        return (
            <div className={classnames(`${baseClassName}`, className)}>
                <main className={classnames(`${baseClassName}__content`)}>
                    <div className={`${baseClassName}__support-cards`}>
                        <SupportCards onClick={handleOptionCardClick} selectedOption={internalValue} />
                    </div>
                </main>
                <footer className={classnames(`${baseClassName}__footer`)}>
                    <div className={classnames(`${baseClassName}__action-buttons`)}>
                        <ByzzerButton label={'Submit'} onClick={handleSubmitClick} />
                        {showSkipButton && (
                            <ByzzerButton label={'Snooze'} type={'negative'} onClick={handleSkipClick} />
                        )}
                    </div>
                </footer>
            </div>
        );
    }
);

export default SupportContent;

SupportContent.displayName = 'SupportContent';
